import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'Store/index';
import { actions } from 'Store/session';
import Icon from 'Components/_NewElements/Icon';
import ToolTip from 'Components/ToolTip';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { AttachmentForm } from '../_NewElements/GeneralForms/AttachmentsForm';
import React from 'react';

const NewMessage = ({ postNewDiscussion }: INewMessage) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const dispatch = useAppDispatch();
  const newMessage = useAppSelector(s => s.session.messagingSessionSettings.newMessage);

  const newMessageChange = (newMessage: Record<'title' | 'body', string> | undefined) => {
    dispatch(actions.setMessagesSessionSettings({ newMessage }));
  };

  // Attachments that have been inputted through the attachments form, ready to be sent
  const [acceptedAttachments, setAcceptedAttachments] = React.useState<File[]>([]);

  // Temporary attachments used for handling the attachment form
  const [tempAttachments, setTempAttachments] = React.useState<File[] | undefined>(undefined);
  const handleTempFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (Array.isArray(e.target.files) && e.target.files[0]) {
      setTempAttachments([...(tempAttachments ?? []), e.target.files[0]]);
    }
  };

  return newMessage ? (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <div>{fm('messaging.messageTitle')}</div>
        <div style={{ width: '30rem' }}>
          <InputHandler
            name="newMessageTitle"
            type="TextField"
            formData={{
              document: { newMessageTitle: newMessage['title'] },
              onChange: values => {
                newMessageChange({ ...newMessage, title: values['newMessageTitle'] as string });
              },
            }}
            editing
            placeholder={'messaging.messageTitle'}
            fullWidth
          />
        </div>
      </div>
      <div style={{ marginBottom: '2rem' }}>
        <div>{fm('messaging.messageBody')}</div>
        <div>
          <InputHandler
            name="newMessageBody"
            type="TextArea"
            formData={{
              document: { newMessageBody: newMessage['body'] },
              onChange: values => {
                newMessageChange({ ...newMessage, body: values['newMessageBody'] as string });
              },
            }}
            editing
            placeholder={'messaging.messageBody'}
            rows={4}
          />
        </div>
      </div>
      {acceptedAttachments.length > 0 && (
        <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <div style={{ fontWeight: 600 }}>{fm('messaging.unsentAttachments')}</div>
          {acceptedAttachments.map(file => (
            <Container key={file.name}>
              <Item>{file.name}</Item>
              <Item sx={{ marginLeft: '1rem', cursor: 'pointer' }}>
                <Icon
                  icon="close"
                  color="errorDefault"
                  button
                  onClick={() => {
                    const filteredAttachments = acceptedAttachments.filter(attch => attch.name !== file.name);
                    setAcceptedAttachments(filteredAttachments);
                  }}
                />
              </Item>
            </Container>
          ))}
        </div>
      )}
      <Container>
        <Item xs={6}>
          <ActionButtonRounded
            text="messaging.deleteDraft"
            width={'auto'}
            height={3.5}
            fontSize={16}
            onClick={() => newMessageChange(undefined)}
            border={true}
          />
        </Item>
        <Item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ marginRight: '2rem', display: 'flex', alignItems: 'center' }}>
            <ToolTip
              hover
              title={fm('messaging.addAttachment')}
              content={
                <Item
                  xs={'auto'}
                  style={{ padding: '0 1rem', cursor: 'pointer' }}
                  onClick={() => setTempAttachments([])}
                >
                  <Icon icon="attach" />
                </Item>
              }
            />
          </div>
          <ActionButtonRounded
            text="messaging.send"
            width="auto"
            height={3.5}
            fontSize={18}
            filled
            onClick={() => {
              postNewDiscussion(acceptedAttachments);
              setAcceptedAttachments([]);
            }}
            disabled={!newMessage.title || !newMessage.body}
          />
        </Item>
      </Container>
      <ConfirmationDialog
        open={!!tempAttachments}
        fullWidth={false}
        text={
          <div style={{ minWidth: '40rem' }}>
            {tempAttachments && (
              <AttachmentForm fm={fm} attachmentData={tempAttachments} handleFileChange={handleTempFileChange} />
            )}
          </div>
        }
        cancel={{
          callback: e => {
            e.stopPropagation();
            setTempAttachments(undefined);
          },
        }}
        confirm={{
          disabled: !(tempAttachments && typeof tempAttachments === 'object'),
          text: 'general.accept',
          callback: async e => {
            e.stopPropagation();
            // Add temp attachments to accepted attachments data
            tempAttachments && setAcceptedAttachments([...acceptedAttachments, ...tempAttachments]);
            setTempAttachments(undefined);
          },
        }}
      />
    </>
  ) : undefined;
};

interface INewMessage {
  postNewDiscussion: (attachments?: File[]) => void;
}

export default NewMessage;
